import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'api/config';
import { produce } from 'immer';
import { logError } from 'lib/sentry/logError';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';

import { IPlanningSprint } from 'features/planning/Sprint';
import { useUserId } from 'features/user/hooks/useUserId';

import { usePlanningActiveFinishedSprintsQueryActions } from '../query/activeFinishedSprints/useActiveFinishedSprintsActions';

export const useDeleteCoachingToolFromFinishedSprintMutation = () => {
  const queryClient = useQueryClient();
  const userId = useUserId();
  const { request } = useMutationHTTPRequest();

  const {
    updateActiveFinishedSprints,
    cancelActiveFinishedSprintsQueries,
    getActiveFinishedSprints,
  } = usePlanningActiveFinishedSprintsQueryActions();

  return useMutation(
    ({ toolId, sprintId }: { toolId: string; sprintId: string }) =>
      request({
        url: `/api/web/sprint-planning/coaching-tools/remove`,
        body: { toolId, sprintId },
      }),
    {
      onMutate: async ({ toolId, sprintId }) => {
        await cancelActiveFinishedSprintsQueries();

        const previousActiveFinishedValue = getActiveFinishedSprints();
        const prevSprintItems = previousActiveFinishedValue!.sprints;

        if (previousActiveFinishedValue) {
          const sprintIndex = prevSprintItems.findIndex(
            (sprint) => sprint.sprintId === sprintId
          );

          if (sprintIndex !== -1) {
            const updatedSprints = produce(prevSprintItems, (draft) => {
              const coachingToolIndex = draft[
                sprintIndex
              ].coachingTools.findIndex((tool) => tool.id === toolId);
              draft[sprintIndex].coachingTools.splice(coachingToolIndex, 1);
            });

            updateActiveFinishedSprints(updatedSprints);
          }
        }

        return { previousActiveFinishedValue };
      },
      onError: (
        err: Error,
        __: any,
        context:
          | {
              previousActiveFinishedValue?: {
                sprints: IPlanningSprint[];
              };
            }
          | undefined
      ) => {
        if (context?.previousActiveFinishedValue) {
          queryClient.setQueryData(
            [queryKeys.draftSprints, userId],
            context.previousActiveFinishedValue
          );
        }
        logError(err);
      },
    }
  );
};
